/**
 * If user has a selected exam, redirect to the exam-scoped URL (e.g. /exams/telc-b1/practice).
 * Otherwise render the fallback.
 * Use for routes like /practice, /lessons, /exam so old links get SEO-friendly URLs.
 * @param {string} fromPath - Optional. If set, any path segment after this in the current URL is appended (e.g. fromPath="lessons" for /lessons/b1 -> /exams/slug/lessons/b1).
 */
import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { getExamSlug } from '../../utils/examSlug';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';

export default function RedirectToExamScoped({ selectedExamData, isLoadingExamData, toPath, fromPath, children }) {
  const { lang } = useParams();
  const location = useLocation();
  const l = lang || 'en';

  // App.jsx fetches the exam selection asynchronously. Falling through to
  // `children` while that is still in flight renders NoExamSelected at someone
  // who does have an exam — the message they then see for the whole load.
  // ExamSlugRoute already waits like this for the /exams/:slug routes.
  if (isLoadingExamData && !selectedExamData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
      </div>
    );
  }

  if (selectedExamData?.examProvider && selectedExamData?.level) {
    const slug = getExamSlug(selectedExamData.examProvider, selectedExamData.level);
    if (slug) {
      let suffix = '';
      if (fromPath && location.pathname.includes(`/${fromPath}`)) {
        const after = location.pathname.split(`/${fromPath}`)[1] || '';
        suffix = after; // e.g. /b1
      }
      const target = toPath ? `/exams/${slug}/${toPath}${suffix}` : `/exams/${slug}${suffix}`;
      const search = location.search || '';
      // Preserve location.state (e.g. results + examData when opening from exam history) so exam results page receives the attempt
      return <Navigate to={`/${l}${target}${search}`} state={location.state} replace />;
    }
  }

  return children;
}
